import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../Layout/DashboardLayout';
import LoadingSpinner from '../Common/LoadingSpinner';
import SuccessOverlay from '../Common/SuccessOverlay';
import { requestHelpers } from '../../services/api';
import './Campaigns.css';

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [listNames, setListNames] = useState([]);
  const [availableMedia, setAvailableMedia] = useState([]);
  const [headerTypeRequired, setHeaderTypeRequired] = useState(null);
  const [selectedMediaId, setSelectedMediaId] = useState('');
  const [selectedList, setSelectedList] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [detectedVariables, setDetectedVariables] = useState([]);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [templateCategory, setTemplateCategory] = useState('ALL');
  const [templatePreview, setTemplatePreview] = useState(null);
  const { user, refreshProfile } = useAuth();

  const [newCampaign, setNewCampaign] = useState({
    name: '',
    messageType: 'text',
    messageText: '',
    templateId: '',
    templateName: '',
    variableMappings: {},
    contactIds: [],
    listName: '',
    scheduledAt: ''
  });
  useEffect(() => {
    fetchCampaigns();
    fetchListNames();
    fetchApprovedTemplates();
    fetchMedia();
  }, [filters]);

  // Real-time socket listener for live stats
  useEffect(() => {
    if (!window.socket) return;

    const handleStatUpdate = (update) => {
      console.log('📡 [SOCKET] Live update received:', update);
      setCampaigns(prev => prev.map(c => {
        if (c._id === update.campaignId) {
          // 1. Handle overall campaign status/progress updates
          if (update.status === 'completed' || update.status === 'running') {
            const isJustFinished = update.status === 'completed' && c.status !== 'completed';
            if (isJustFinished) {
              setShowConfetti(true);
              toast.success(`🎉 Campaign "${c.name}" completed successfully!`, {
                position: "top-center",
                autoClose: 5000,
                theme: "colored"
              });
            }
            return {
              ...c,
              status: update.status,
              execution: {
                ...c.execution,
                progress: update.progress || (update.status === 'completed' ? 100 : c.execution?.progress || 0)
              }
            };
          }

          // 2. Handle individual message status counts
          const mapping = {
            'sent': 'messagesSent',
            'delivered': 'messagesDelivered',
            'read': 'messagesRead',
            'failed': 'messagesFailed'
          };
          const field = mapping[update.status];
          if (field) {
            return {
              ...c,
              analytics: {
                ...c.analytics,
                [field]: (c.analytics[field] || 0) + 1
              }
            };
          }
        }
        return c;
      }));
    };

    window.socket.on('campaignStatusUpdate', handleStatUpdate);
    return () => window.socket.off('campaignStatusUpdate', handleStatUpdate);
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await requestHelpers.get('/campaigns', {
        params: filters
      });
      const fetchedCampaigns = response.data.campaigns || [];
      setCampaigns(fetchedCampaigns);
    } catch (error) {
      toast.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchListNames = async () => {
    try {
      const response = await requestHelpers.get('/contacts/list-names');
      setListNames(response.data || []);
    } catch (error) {
      console.error('Failed to fetch list names');
    }
  };

  const fetchApprovedTemplates = async () => {
    try {
      await requestHelpers.get('/templates/sync');
      const response = await requestHelpers.get('/templates', {
        params: { status: 'APPROVED' }
      });
      setAvailableTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await requestHelpers.get('/media');
      setAvailableMedia(response.data || []);
    } catch (error) {
      console.error('Failed to fetch media:', error);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: newCampaign.name,
        type: 'broadcast',
        message: {
          type: newCampaign.messageType,
          content: newCampaign.messageType === 'text'
            ? { text: newCampaign.messageText }
            : {
              templateId: newCampaign.templateId,
              templateName: newCampaign.templateName,
              variableMappings: newCampaign.variableMappings,
              mediaId: selectedMediaId || undefined,
              mediaType: headerTypeRequired || undefined
            }
        },
        targeting: {
          listName: selectedList
        },
        scheduledAt: newCampaign.scheduledAt ? new Date(newCampaign.scheduledAt).toISOString() : null
      };

      const priceType = newCampaign.messageType === 'text' ? 'text' : (availableTemplates.find(t => t._id === newCampaign.templateId)?.category?.toLowerCase() || 'text');
      const rate = user?.messagePricing?.[priceType] || user?.messagePricing?.['text'] || 0.10;
      const count = listNames.find(l => l.name === selectedList)?.count || 0;
      const estimatedTotal = rate * count * 1.18;

      if (user?.walletBalance < estimatedTotal) {
        toast.error(`Insufficient balance. Recharge needed (Est: ₹${estimatedTotal.toFixed(2)})`);
        return;
      }

      await requestHelpers.post('/campaigns', payload);
      setShowConfetti(true);
      toast.success(newCampaign.scheduledAt ? '📅 Campaign scheduled successfully!' : '🚀 Campaign launched successfully!', {
        position: "top-center",
        autoClose: 5000,
        theme: "colored"
      });
      setShowCreateModal(false);
      resetNewCampaign();
      fetchCampaigns();
      // Refresh user profile to update wallet balance
      refreshProfile().catch(() => {});
    } catch (error) {
      toast.error(error.message || 'Failed to create campaign');
    }
  };

  const resetNewCampaign = () => {
    setNewCampaign({
      name: '',
      messageType: 'text',
      messageText: '',
      templateId: '',
      templateName: '',
      variableMappings: {},
      listName: '',
      scheduledAt: ''
    });
    setSelectedList('');
    setTemplatePreview(null);
    setTemplateCategory('ALL');
  };

  const handleTemplateSelect = (templateId) => {
    const tmpl = availableTemplates.find(t => t._id === templateId);
    if (!tmpl) return;

    // Parse variables from template content
    const body = tmpl?.components?.find(c => c.type === 'BODY')?.text || tmpl?.content || '';
    const variableMatches = body.match(/\{\{(\w+)\}\}/g) || [];
    const vars = [...new Set(variableMatches.map(v => v.slice(2, -2)))];
    setDetectedVariables(vars);

    // Initialize mappings
    const initialMappings = {};
    vars.forEach(v => {
      initialMappings[v] = { type: 'contact_field', value: 'name' };
    });

    setNewCampaign(prev => ({
      ...prev,
      templateId,
      templateName: tmpl.name,
      variableMappings: initialMappings
    }));

    // Check if template needs a media header
    const header = tmpl?.components?.find(c => c.type === 'HEADER');
    if (header && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format)) {
      setHeaderTypeRequired(header.format.toLowerCase());
    } else {
      setHeaderTypeRequired(null);
      setSelectedMediaId('');
    }

    // Set Preview Content
    setTemplatePreview(tmpl);
  };

  const handleCampaignAction = async (campaignId, action) => {
    try {
      await requestHelpers.post(`/campaigns/${campaignId}/${action}`);
      toast.success(`Action ${action} successful`);
      fetchCampaigns();
    } catch (error) {
      toast.error(`Action ${action} failed`);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await requestHelpers.delete(`/campaigns/${campaignId}`);
      toast.success('Campaign removed');
      fetchCampaigns();
    } catch (error) {
      toast.error('Deletion failed');
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'running': return 'badge-running';
      case 'completed': return 'badge-success';
      case 'scheduled': return 'badge-warning';
      case 'failed': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  const handleRetryCampaign = async (campaignId) => {
    try {
      await requestHelpers.post(`/campaigns/${campaignId}/retry`);
      toast.success('Retrying failed messages...');
      fetchCampaigns();
    } catch (error) {
      toast.error('Retry failed');
    }
  };

  return (
    <DashboardLayout>
      <SuccessOverlay 
        active={showConfetti} 
        onComplete={() => setShowConfetti(false)} 
        title={newCampaign.scheduledAt ? 'Campaign Scheduled!' : 'Campaign Launched!'}
        subtitle={newCampaign.scheduledAt 
          ? `Your campaign will start on ${new Date(newCampaign.scheduledAt).toLocaleString()}`
          : 'Your messages are on their way to your audience.'}
      />
      <div className="campaigns-page">
        <div className="page-header">
          <div className="header-content">
            <h1>Campaigns</h1>
            <p>Track your real-time outreach performance</p>
            {user?.whatsappNumber && (
              <div className="header-number-badge mt-2" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>
                <i className="fab fa-whatsapp"></i>
                <span>Linked to: {user.whatsappNumber}</span>
              </div>
            )}
          </div>
          <div className="header-actions">
            <button className="btn btn--primary" onClick={() => setShowCreateModal(true)}>
              <i className="fas fa-plus"></i> Launch Campaign
            </button>
          </div>
        </div>

        <div className="campaigns-filters">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search by name..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="status-filter"
          >
            <option value="all">Global View</option>
            <option value="running">Currently Running</option>
            <option value="completed">Completed Projects</option>
            <option value="scheduled">Upcoming Tasks</option>
            <option value="paused">On Hold</option>
          </select>
        </div>

        <div className="campaigns-grid">
          {loading && campaigns.length === 0 ? (
            <LoadingSpinner />
          ) : campaigns.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-bullhorn"></i>
              <h3>Ready to start?</h3>
              <p>Launch your first broadcast to engage your audience instantly.</p>
              <button className="btn btn--primary" onClick={() => setShowCreateModal(true)}>
                New Campaign
              </button>
            </div>
          ) : (
            campaigns.map(campaign => (
              <div 
                key={campaign._id} 
                className="campaign-card" 
                onClick={() => {
                  setSelectedCampaignId(campaign._id);
                  setShowAnalytics(true);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="campaign-header">
                  <div className="campaign-title">
                    <h3>{campaign.name}</h3>
                    <span className={`status-badge ${getStatusBadge(campaign.status)}`}>
                      <i className={`fas ${campaign.status === 'running' ? 'fa-spinner fa-spin' : 'fa-circle'}`}></i> {campaign.status}
                    </span>
                  </div>
                  <div className="campaign-actions">
                    <button className="action-btn" onClick={() => handleDeleteCampaign(campaign._id)}>
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>

                <div className="campaign-message">
                  <p>
                    {campaign.message.type === 'text' 
                      ? campaign.message.content.text 
                      : <><i className="fas fa-file-invoice mr-2"></i> {campaign.message.content.templateName}</>
                    }
                  </p>
                </div>

                <div className="campaign-stats">
                  <div className="stats-counts">
                    <div className="stat-item count-box">
                      <span className="stat-label">Sent</span>
                      <span className="stat-value">{campaign.analytics.messagesSent || 0}</span>
                    </div>
                    <div className="stat-item count-box">
                      <span className="stat-label">Delivered</span>
                      <span className="stat-value">{campaign.analytics.messagesDelivered || 0}</span>
                    </div>
                    <div className="stat-item count-box">
                      <span className="stat-label">Read</span>
                      <span className="stat-value">{campaign.analytics.messagesRead || 0}</span>
                    </div>
                    <div className="stat-item count-box">
                      <span className="stat-label">Failed</span>
                      <span className="stat-value">{campaign.analytics.messagesFailed || 0}</span>
                    </div>
                  </div>
                  
                  <div className="stats-rates">
                    <div className="rate-item">
                      <span className="rate-label">Success</span>
                      <span className="rate-value">{Math.round(campaign.successRate || 0)}%</span>
                    </div>
                    <div className="rate-item">
                      <span className="rate-label">Read Rate</span>
                      <span className="rate-value">{Math.round((campaign.analytics.messagesRead / (campaign.analytics.messagesDelivered || 1)) * 100)}%</span>
                    </div>
                    <div className="rate-item">
                      <span className="rate-label">Retention</span>
                      <span className="rate-value">{Math.round(campaign.analytics.retentionRate || 0)}%</span>
                    </div>
                  </div>
                </div>

                {campaign.execution?.progress > 0 && campaign.execution?.progress < 100 && (
                  <div className="campaign-progress-container">
                    <div className="progress-info">
                      <span>Broadcast Progress</span>
                      <span>{Math.round(campaign.execution.progress)}%</span>
                    </div>
                    <div className="progress-bar-wrapper">
                      <div className="progress-bar-fill" style={{ width: `${campaign.execution.progress}%` }}></div>
                    </div>
                  </div>
                )}

                <div className="campaign-footer">
                  <div className="campaign-date">
                    <small>
                      <i className="far fa-clock"></i> {formatDate(campaign.scheduledAt || campaign.createdAt)}
                    </small>
                  </div>
                  <div className="campaign-controls">
                    {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                      <button className="btn btn--sm btn--primary" onClick={() => handleCampaignAction(campaign._id, 'start')}>
                        <i className="fas fa-play mr-1"></i> Launch
                      </button>
                    )}
                    {campaign.status === 'paused' && (
                      <button className="btn btn--sm btn--primary" onClick={() => handleCampaignAction(campaign._id, 'resume')}>
                        <i className="fas fa-play mr-1"></i> Resume
                      </button>
                    )}
                    {campaign.status === 'running' && (
                      <button className="btn btn--sm btn--outline" onClick={() => handleCampaignAction(campaign._id, 'pause')}>
                        <i className="fas fa-pause mr-1"></i> Pause
                      </button>
                    )}
                    {(campaign.status === 'completed' || campaign.status === 'failed') && 
                      (campaign.analytics.messagesFailed || 0) > 0 && 
                      (campaign.analytics.messagesDelivered || 0) < (campaign.targeting?.contactIds?.length || 0) && (
                      <button className="btn btn--sm btn--warning" onClick={() => handleRetryCampaign(campaign._id)}>
                        <i className="fas fa-redo mr-1"></i> Retry Failed
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create Campaign Modal (Glassmorphic) */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>New Campaign</h2>
                <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleCreateCampaign} className="campaign-form">
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={newCampaign.name}
                    onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                    placeholder="E.g. Summer Promo 2024"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Message Content</label>
                  <select
                    value={newCampaign.messageType}
                    onChange={(e) => setNewCampaign({ ...newCampaign, messageType: e.target.value })}
                  >
                    <option value="text">Custom Text</option>
                    {user?.enabledFeatures?.includes('templates') && (
                      <option value="template">Official Template</option>
                    )}
                  </select>
                </div>

                {newCampaign.messageType === 'text' ? (
                  <div className="form-group">
                    <label>Compose Message</label>
                    <textarea
                      value={newCampaign.messageText}
                      onChange={(e) => setNewCampaign({ ...newCampaign, messageText: e.target.value })}
                      placeholder="Write your broadcast message here..."
                      rows="4"
                      required
                    />
                  </div>
                ) : (
                  <div className="form-group">
                    <label>Pre-approved Template</label>
                    
                    <select
                      value={newCampaign.templateId}
                      onChange={(e) => handleTemplateSelect(e.target.value)}
                      required
                      className="form-control"
                    >
                      <option value="">Select approved template...</option>
                      {availableTemplates
                        .filter(t => templateCategory === 'ALL' || t.category?.toUpperCase() === templateCategory)
                        .map(tmpl => (
                          <option key={tmpl._id} value={tmpl._id}>{tmpl.displayName}</option>
                        ))}
                    </select>

                    {/* Template Preview */}
                    {templatePreview && (
                      <div className="template-preview-container animate-fade-in">
                        <div className="whatsapp-message">
                          <div className="whatsapp-body">
                            {(() => {
                              let body = templatePreview.components?.find(c => c.type === 'BODY')?.text || templatePreview.content || '';
                              // Replace variables with mapped labels
                              Object.entries(newCampaign.variableMappings).forEach(([varName, mapping]) => {
                                const label = mapping.type === 'contact_field' 
                                  ? `[${mapping.value.charAt(0).toUpperCase() + mapping.value.slice(1)}]`
                                  : `[${mapping.value || 'Custom Value'}]`;
                                body = body.replaceAll(`{{${varName}}}`, label);
                              });
                              return body;
                            })()}
                          </div>
                          <div className="whatsapp-timestamp">
                            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {newCampaign.messageType === 'template' && detectedVariables.length > 0 && (
                  <div className="variable-mapping-section animate-fade-in" style={{ background: 'rgba(59, 130, 246, 0.05)', padding: '1.25rem', borderRadius: '15px', border: '1px solid rgba(59, 130, 246, 0.2)', marginBottom: '1.5rem' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '1rem', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="fas fa-magic"></i> Variable Auto-Mapping
                    </h4>
                    {detectedVariables.map((varName) => (
                      <div key={varName} className="mapping-row mb-3" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                          <code style={{ background: '#e0e7ff', padding: '2px 6px', borderRadius: '4px', color: '#4338ca' }}>
                            {`{{${varName}}}`}
                          </code>
                        </span>
                        <select
                          value={newCampaign.variableMappings[varName]?.value || 'name'}
                          onChange={(e) => {
                            const val = e.target.value;
                            setNewCampaign(prev => ({
                              ...prev,
                              variableMappings: {
                                ...prev.variableMappings,
                                [varName]: val === 'custom' 
                                  ? { type: 'custom', value: '' }
                                  : { type: 'contact_field', value: val }
                              }
                            }));
                          }}
                          style={{ margin: 0, padding: '8px' }}
                        >
                          <option value="name">Contact Name</option>
                          <option value="firstname">First Name</option>
                          <option value="email">Email Address</option>
                          <option value="phone">Phone Number</option>
                          <option value="custom">Fixed Text / Custom</option>
                        </select>
                        {newCampaign.variableMappings[varName]?.type === 'custom' && (
                          <input
                            type="text"
                            placeholder="Type custom value..."
                            value={newCampaign.variableMappings[varName].value}
                            onChange={(e) => {
                              const val = e.target.value;
                              setNewCampaign(prev => ({
                                ...prev,
                                variableMappings: {
                                ...prev.variableMappings,
                                [varName]: { type: 'custom', value: val }
                                }
                              }));
                            }}
                            className="mt-2"
                            style={{ gridColumn: '1 / span 2', padding: '8px' }}
                          />
                        )}
                      </div>
                    ))}
                    <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '8px', fontStyle: 'italic' }}>
                      <i className="fas fa-info-circle mr-1"></i> Values will be automatically extracted from each contact's data during broadcast.
                    </p>
                  </div>
                )}

                {newCampaign.messageType === 'template' && headerTypeRequired && (
                  <div className="form-group animate-fade-in" style={{ background: 'rgba(255,165,0,0.05)', padding: '1.25rem', borderRadius: '15px', border: '1px solid rgba(255,165,0,0.2)', marginBottom: '1.5rem' }}>
                    <label className="d-flex justify-content-between align-items-center mb-2">
                      <span style={{ fontWeight: '600', color: '#854d0e' }}>
                        <i className={`fas ${headerTypeRequired === 'image' ? 'fa-image' : headerTypeRequired === 'video' ? 'fa-video' : 'fa-file-pdf'} mr-2`}></i>
                        Header {headerTypeRequired.charAt(0).toUpperCase() + headerTypeRequired.slice(1)} Required
                      </span>
                      <a href="/media-library" target="_blank" style={{ fontSize: '11px', color: 'var(--color-primary)', textDecoration: 'underline' }}>
                        Manage Assets <i className="fas fa-external-link-alt ml-1"></i>
                      </a>
                    </label>
                    <select
                      value={selectedMediaId}
                      onChange={(e) => setSelectedMediaId(e.target.value)}
                      required
                      style={{ borderColor: 'rgba(255,165,0,0.3)' }}
                    >
                      <option value="">Select a {headerTypeRequired} from library...</option>
                      {availableMedia
                        .filter(m => m.fileType === headerTypeRequired)
                        .map(media => (
                          <option key={media.mediaId} value={media.mediaId}>
                            {media.fileName} ({media.daysRemaining} days left)
                          </option>
                        ))
                      }
                    </select>
                    {availableMedia.filter(m => m.fileType === headerTypeRequired).length === 0 && (
                      <div style={{ marginTop: '10px', fontSize: '12px', color: '#991b1b', background: 'rgba(239, 68, 68, 0.1)', padding: '8px', borderRadius: '8px' }}>
                        <i className="fas fa-exclamation-triangle mr-1"></i>
                        No {headerTypeRequired}s available. <a href="/media-library" style={{ fontWeight: 'bold', textDecoration: 'underline' }}>Upload one here</a> first.
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label>Audience List</label>
                  <select
                    value={selectedList}
                    onChange={(e) => setSelectedList(e.target.value)}
                    required
                  >
                    <option value="">Select contact list...</option>
                    {listNames.map(list => (
                      <option key={list.name} value={list.name}>{list.name} ({list.count} contacts)</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Schedule Execution</label>
                  <input
                    type="datetime-local"
                    value={newCampaign.scheduledAt}
                    onChange={(e) => setNewCampaign({ ...newCampaign, scheduledAt: e.target.value })}
                  />
                </div>

                {/* Detailed Cost Estimation Box */}
                <div className="cost-estimation-box mt-4 p-3 rounded" style={{ background: 'rgba(var(--color-primary-rgb), 0.05)', border: '1px solid rgba(var(--color-primary-rgb), 0.2)' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-calculator text-primary"></i> Estimated Investment
                  </h4>
                  
                  {(() => {
                    const template = availableTemplates.find(t => t._id === newCampaign.templateId);
                    const category = template?.category?.toLowerCase() || 'template';
                    const priceType = newCampaign.messageType === 'text' ? 'text' : category;
                    
                    const rate = user?.messagePricing?.[priceType] || user?.messagePricing?.['text'] || 0.10;
                    const count = listNames.find(l => l.name === selectedList)?.count || 0;
                    const subtotal = rate * count;
                    const gst = subtotal * 0.18;
                    const total = subtotal + gst;

                    return (
                      <div className="estimation-details" style={{ fontSize: '13px' }}>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-secondary">Message Type:</span>
                          <span className="font-weight-bold" style={{ textTransform: 'capitalize', color: 'var(--color-primary)' }}>
                             {priceType} {newCampaign.messageType === 'template' ? 'Conversation' : 'Message'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-secondary">Base Rate:</span>
                          <span className="font-weight-bold">₹{rate.toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-secondary">Expected Reach:</span>
                          <span className="font-weight-bold">{count} contacts</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2 pt-2 border-top">
                          <span className="text-secondary">Subtotal:</span>
                          <span className="font-weight-bold">₹{subtotal.toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-secondary">GST (18%):</span>
                          <span className="font-weight-bold">₹{gst.toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between mt-3 pt-2 border-top" style={{ fontSize: '16px', color: 'var(--color-primary)' }}>
                          <span className="font-weight-bold">Total Estimate:</span>
                          <span className="font-weight-bold">₹{total.toFixed(2)}</span>
                        </div>
                        
                        {user?.walletBalance < total && (
                          <div className="alert alert-danger mt-3 mb-0 py-2 d-flex align-items-center gap-2" style={{ fontSize: '12px' }}>
                            <i className="fas fa-exclamation-triangle"></i>
                            <span>Insufficient balance (Current: ₹{user.walletBalance.toFixed(2)})</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div className="form-actions mt-4">
                  <button type="button" className="btn btn--secondary" onClick={() => setShowCreateModal(false)}>Discard</button>
                  <button 
                    type="submit" 
                    className="btn btn--primary"
                    disabled={(() => {
                        const template = availableTemplates.find(t => t._id === newCampaign.templateId);
                        const category = template?.category?.toLowerCase() || 'template';
                        const priceType = newCampaign.messageType === 'text' ? 'text' : category;
                        const rate = user?.messagePricing?.[priceType] || user?.messagePricing?.['text'] || 0.10;
                        const count = listNames.find(l => l.name === selectedList)?.count || 0;
                        return user?.walletBalance < (rate * count * 1.18);
                    })()}
                  >
                    {newCampaign.scheduledAt ? (
                      <><i className="fas fa-clock mr-1"></i> Schedule Campaign</>
                    ) : (
                      <><i className="fas fa-rocket mr-1"></i> Launch Campaign</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        <CampaignAnalyticsModal 
          isOpen={showAnalytics}
          onClose={() => setShowAnalytics(false)}
          campaignId={selectedCampaignId}
        />
      </div>
    </DashboardLayout>
  );
};

export default Campaigns;

const CampaignAnalyticsModal = ({ isOpen, onClose, campaignId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && campaignId) {
      fetchAnalytics();
    }
  }, [isOpen, campaignId]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await requestHelpers.get(`/campaigns/${campaignId}/analytics`);
      setData(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 2000 }}>
      <div className="modal-content analytics-modal" style={{ maxWidth: '800px', width: '95%', maxHeight: '90vh', overflowY: 'auto', borderRadius: '24px', position: 'relative' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ padding: '24px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '800', margin: 0 }}>Campaign Performance</h2>
            <p className="subtitle" style={{ color: '#6b7280', fontSize: '14px', marginTop: '4px' }}>{data?.summary?.name || 'Loading details...'}</p>
          </div>
          <button className="modal-close" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>

        {loading ? (
          <div className="p-5 text-center"><LoadingSpinner /></div>
        ) : (
          <div className="analytics-body" style={{ padding: '24px' }}>
            <div className="analytics-grid-stats mb-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div className="stat-card-premium" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', padding: '20px', borderRadius: '16px', border: '1px solid #bae6fd' }}>
                <span className="label" style={{ display: 'block', fontSize: '12px', color: '#0369a1', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px' }}>Retention (30d)</span>
                <span className="value" style={{ fontSize: '28px', fontWeight: '900', color: '#0c4a6e' }}>{data?.summary?.retentionRate || 0}%</span>
                <div className="progress-mini" style={{ height: '6px', background: 'rgba(3, 105, 161, 0.1)', borderRadius: '3px', marginTop: '12px' }}>
                  <div className="fill" style={{ width: `${data?.summary?.retentionRate || 0}%`, height: '100%', background: '#0369a1', borderRadius: '3px' }}></div>
                </div>
              </div>
              <div className="stat-card-premium" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', padding: '20px', borderRadius: '16px', border: '1px solid #bbf7d0' }}>
                <span className="label" style={{ display: 'block', fontSize: '12px', color: '#15803d', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px' }}>Interaction Rate</span>
                <span className="value" style={{ fontSize: '28px', fontWeight: '900', color: '#064e3b' }}>{data?.summary?.interactionRate || 0}%</span>
                <div className="progress-mini" style={{ height: '6px', background: 'rgba(21, 128, 61, 0.1)', borderRadius: '3px', marginTop: '12px' }}>
                  <div className="fill" style={{ width: `${data?.summary?.interactionRate || 0}%`, height: '100%', background: '#15803d', borderRadius: '3px' }}></div>
                </div>
              </div>
              <div className="stat-card-premium" style={{ background: 'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)', padding: '20px', borderRadius: '16px', border: '1px solid #fef08a' }}>
                <span className="label" style={{ display: 'block', fontSize: '12px', color: '#a16207', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px' }}>Read Rate</span>
                <span className="value" style={{ fontSize: '28px', fontWeight: '900', color: '#713f12' }}>
                  {Math.round((data?.summary?.messagesRead || 0) / (data?.summary?.messagesDelivered || 1) * 100)}%
                </span>
                <div className="progress-mini" style={{ height: '6px', background: 'rgba(161, 98, 7, 0.1)', borderRadius: '3px', marginTop: '12px' }}>
                  <div className="fill" style={{ width: `${((data?.summary?.messagesRead || 0) / (data?.summary?.messagesDelivered || 1)) * 100}%`, height: '100%', background: '#a16207', borderRadius: '3px' }}></div>
                </div>
              </div>
            </div>

            <div className="analytics-section mb-4" style={{ background: '#fff', padding: '20px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fas fa-mouse-pointer" style={{ color: '#3b82f6' }}></i> Button/Link Engagement
              </h3>
              <div className="button-stats-list">
                {Object.keys(data.buttonClicks || {}).length === 0 ? (
                  <p className="empty-text" style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>No buttons sent or clicked yet.</p>
                ) : (
                  Object.entries(data.buttonClicks).map(([btn, count]) => (
                    <div key={btn} className="button-stat-row" style={{ display: 'grid', gridTemplateColumns: 'minmax(100px, 1fr) 2fr 80px', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                      <span className="btn-name" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{btn}</span>
                      <div className="btn-bar-wrapper" style={{ height: '8px', background: '#f3f4f6', borderRadius: '4px' }}>
                        <div className="btn-bar-fill" style={{ width: `${Math.min((count / (data.summary.messagesSent || 1)) * 100, 100)}%`, height: '100%', background: '#3b82f6', borderRadius: '4px' }}></div>
                      </div>
                      <span className="btn-count" style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', textAlign: 'right' }}>{count} clicks</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="analytics-section" style={{ background: '#fff', padding: '0', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6', overflow: 'hidden' }}>
              <div style={{ padding: '20px', borderBottom: '1px solid #f3f4f6' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-users" style={{ color: '#8b5cf6' }}></i> Interactions Log
                </h3>
              </div>
              <div className="engagement-table-wrapper" style={{ overflowX: 'auto' }}>
                <table className="engagement-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: '#f9fafb' }}>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '12px 20px', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Contact</th>
                      <th style={{ textAlign: 'left', padding: '12px 20px', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Action</th>
                      <th style={{ textAlign: 'left', padding: '12px 20px', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Detail</th>
                      <th style={{ textAlign: 'left', padding: '12px 20px', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.engagementLog?.length === 0 ? (
                      <tr><td colSpan="4" className="text-center p-4" style={{ textAlign: 'center', color: '#9ca3af', padding: '40px' }}>No interactions recorded yet.</td></tr>
                    ) : (
                      data.engagementLog.map((log, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td className="contact-cell" style={{ padding: '12px 20px' }}>
                            <div style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>{log.contact?.name || 'Unknown'}</div>
                            <div style={{ color: '#6b7280', fontSize: '12px' }}>{log.contact?.phone}</div>
                          </td>
                          <td style={{ padding: '12px 20px' }}>
                            <span className={`action-badge ${log.type}`} style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '4px',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: '700',
                              background: log.type === 'click' ? '#eff6ff' : '#f5f3ff',
                              color: log.type === 'click' ? '#1d4ed8' : '#6d28d9'
                            }}>
                              {log.type === 'click' ? <i className="fas fa-hand-pointer"></i> : <i className="fas fa-reply"></i>}
                              {log.type.toUpperCase()}
                            </span>
                          </td>
                          <td className="detail-cell" style={{ padding: '12px 20px', fontSize: '13px', color: '#374151' }}>{log.detail}</td>
                          <td className="time-cell" style={{ padding: '12px 20px', fontSize: '12px', color: '#6b7280' }}>{new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};